"use client"

import { VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import Masonry from "@/components/Masonry/Masonry"
import { type GalleryBlock as GalleryBlockType } from "@/types/database"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface GalleryBlockProps extends ContentBlockTitleNumberProps {
  block: GalleryBlockType
}

const GalleryBlock: FC<GalleryBlockProps> = ({ block, titleNumber }) => {
  const {
    darker_bg,
    headline,
    image_size,
    show_legend,
    title,
    two_columns_gallery = false,
  } = block
  const images = block.images || []
  const trimmedTitle = title?.trim()

  const items = images.map((image) => ({
    alt: image?.directus_files_id?.title || "Image",
    height: image?.directus_files_id?.height || 64,
    id: image?.directus_files_id?.id,
    img: `${process.env.DIRECTUS_ASSETS_URL}${image?.directus_files_id?.id}?quality=90&width=1376&format=webp`,
    title: image?.directus_files_id?.title,
    url: `${process.env.DIRECTUS_ASSETS_URL}${image?.directus_files_id?.id}`,
  }))

  return (
    <VStack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="gallery-block internal-padding"
      gap={{ base: 8, lg: two_columns_gallery ? 6 : 8 }}
    >
      {(title || headline) && (
        <VStack alignItems="flex-start">
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle
              fontWeight="bold"
              title={trimmedTitle}
              titleNumber={titleNumber}
            />
            {headline && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={headline}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}

      {images.length > 0 && (
        <Masonry
          imageSize={image_size}
          items={items}
          showLegend={Boolean(show_legend)}
        />
      )}
    </VStack>
  )
}

export default GalleryBlock
